import * as React from 'react';
import cx from 'classnames';
import ReactSelect, {
  Props as ReactSelectProps,
  ActionMeta,
  MultiValue,
  SingleValue,
} from 'react-select';

import './styles/Select.scss';

type Option<T> = { label: string; value: T };

type CommonProps<T> = Omit<ReactSelectProps<Option<T>>, 'isMulti' | 'onChange' | 'value'> & {
  inline?: boolean;
  full?: boolean;
};
// react-select's `onChange` and `value` types depend on whether the component is being
// used as a multi-select (`isMulti` is true). This discriminated union allows us to
// enforce the correct typing at compile time based on the value of the `isMulti` prop.
type MultiProps<T> = CommonProps<T> & {
  isMulti: true;
  onChange: (value: T[], actionMeta: ActionMeta<Option<T>>) => void;
  value: T[];
};
type SingleProps<T> = CommonProps<T> & {
  isMulti?: false;
  onChange: (value: T | undefined, actionMeta: ActionMeta<Option<T>>) => void;
  value: T | undefined;
};

export type Props<T> = SingleProps<T> | MultiProps<T>;

/**
 * A simple wrapper around react-select's Select component. Supports multi-select and grouped options.
 *
 * A single Option is an object with `{ label: string, value: T }`. The `options` prop accepts an
 * array of single options or option Groups, which are `{ label?: string, options: Option[] }`.
 *
 * Unlike react-select, whose `value` and `onChange` props work with the Option type, Select's
 * `value` and `onChange` props work with _just the value_ of the option: just T (for single select)
 * or T[] (for multi-select).
 *
 * Key differences from the older SelectDeprecated component:
 *
 * 1. Better prop typing
 * 2. Supports grouped options and multi-select
 * 3. Does _not_ support `appendedLinkItem`
 * 4. Boolean prop names are the same as react-select (`isClearable` instead of `clearable`, etc)
 */
function Select<T>({ options = [], inline, full, className, ...props }: Props<T>) {
  // discriminated union types don't work with destructuring, so we
  // have to access `isMulti`, `value` & `onChange` directly from props.
  function handleChange(
    option: MultiValue<Option<T>> | SingleValue<Option<T>>,
    actionMeta: ActionMeta<Option<T>>
  ) {
    if (props.isMulti) {
      props.onChange(
        (option as MultiValue<Option<T>>).map((o) => o.value),
        actionMeta
      );
    } else props.onChange((option as SingleValue<Option<T>>)?.value, actionMeta);
  }

  // find the selected options (`value` prop)
  const flatOptions = options.flatMap((o) => ('options' in o ? o.options : o));
  const optionValue = flatOptions.filter((o) =>
    props.isMulti ? (props.value ?? []).includes(o.value) : o.value === props.value
  );

  return (
    <ReactSelect
      {...props}
      value={optionValue}
      onChange={handleChange}
      options={options}
      className={cx(className, 'Select', inline && 'Select--inline', full && 'Select--full')}
      classNamePrefix="Select"
    />
  );
}

export default Select;
